import React from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import { AlertTriangle, RefreshCw, MessageCircle } from "lucide-react";

/**
 * AppErrorBoundary
 *
 * Wraps the entire app so an unhandled React error renders a friendly
 * "Something went wrong" card instead of a blank cream-coloured screen.
 *
 * - On error we beacon a small report to /api/client-errors (best-effort,
 *   never throws, never blocks rendering)
 * - Shows the same brand styling as the rest of the app
 * - Offers "Try again" (reset + reload current route) and "Back to home"
 * - Surfaces a request id (timestamp + random) so the user can quote it
 *   in support tickets and we can match it to backend logs
 */
export default class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorId: null, errorMessage: null };
  }

  static getDerivedStateFromError(error) {
    // Generate a short reference the user can paste into a support email.
    const errorId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`.toUpperCase();
    return {
      hasError: true,
      errorId,
      errorMessage: (error && (error.message || String(error))) || "Unknown error",
    };
  }

  componentDidCatch(error, info) {
    // Best-effort report. Wrapped in try/catch so reporting failure can
    // never re-throw out of the boundary.
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      if (!backend) return;
      const body = JSON.stringify({
        error_id: this.state.errorId,
        message: (error && error.message) || String(error),
        stack: (error && error.stack) ? String(error.stack).slice(0, 4000) : null,
        component_stack: info && info.componentStack ? String(info.componentStack).slice(0, 4000) : null,
        url: typeof window !== "undefined" ? window.location.href : null,
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        at: new Date().toISOString(),
      });
      // fetch keepalive lets the beacon survive a navigation
      fetch(`${backend}/api/client-errors`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => { /* ignored */ });
    } catch (_) {
      /* ignored */
    }
    // Also log to the browser console so devs see it during dev/preview
    // eslint-disable-next-line no-console
    console.error("[AppErrorBoundary]", error, info);
  }

  handleReset = () => {
    this.setState({ hasError: false, errorId: null, errorMessage: null });
  };

  handleReload = () => {
    if (typeof window !== "undefined") window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-6 py-12" data-testid="app-error-boundary">
        <div className="max-w-md w-full">
          <div className="flex items-center justify-center mb-6">
            <Logo size={48} className="opacity-90" />
          </div>

          <div className="card relative">
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 bg-[#FDF6EC] text-[#8A6D3B] border border-[#E8D9B5] mt-0.5">
                <AlertTriangle size={18} strokeWidth={1.75} />
              </div>
              <div className="flex-1">
                <div className="label-mono mb-1">Something went wrong</div>
                <h1 className="font-serif text-2xl text-[#0D2B52] leading-tight">
                  We&rsquo;ve been notified.
                </h1>
              </div>
            </div>

            <p className="text-[15px] text-[#5a6b7a] leading-relaxed mb-5 font-serif">
              A hiccup stopped this page from loading. Your data is safe and nothing
              you&rsquo;ve done has been lost. The team has been alerted automatically
              and we&rsquo;ll have a fix out shortly.
            </p>

            <div className="bg-[#FAF9F6] border border-[#E5E4DE] px-3 py-2 mb-5">
              <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] mb-1">
                Reference
              </div>
              <div className="font-mono text-[13px] text-[#0D2B52] break-all" data-testid="error-reference-id">
                {this.state.errorId}
              </div>
              <div className="text-[11px] text-[#6B7280] mt-2">
                Quote this if you reach out to support, it helps us find the issue faster.
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={this.handleReload}
                className="btn-teal flex-1 inline-flex items-center justify-center gap-2"
                data-testid="error-try-again"
              >
                <RefreshCw size={14} strokeWidth={2} />
                Try again
              </button>
              <Link
                to="/"
                onClick={this.handleReset}
                className="btn-ghost flex-1 inline-flex items-center justify-center"
                data-testid="error-go-home"
              >
                Back to home
              </Link>
            </div>

            <div className="mt-5 pt-4 border-t border-[#E5E4DE] flex items-center gap-2 text-[12px] text-[#6B7280]">
              <MessageCircle size={12} strokeWidth={1.75} />
              <span>
                Still stuck?{" "}
                <a href="mailto:hello@tenanttrail.net" className="text-[#1B6C62] hover:underline">
                  Email us
                </a>{" "}
                with the reference above.
              </span>
            </div>
          </div>

          <p className="text-center text-[11px] font-mono text-[#9CA3AF] mt-6">
            Protection &middot; Evidence &middot; Accountability.
          </p>
        </div>
      </div>
    );
  }
}
